import React, { useMemo} from "react";
import Select from "react-select";
import { SidePopup } from "../../components/model-popup/side-popup";
import { CustomButton } from "../../components/buttons/button";
import SelectInput from "../../components/custom-fields/select";
import TextIconInput from "../../components/custom-fields/input";
import ErrorMessage from "../../components/custom-fields/error-message";
export type CreateRuleOption = { label: string; value: string };

export type CreateRuleModalProps = {
  open: boolean;
  /** Called for Cancel, X, and after a successful close flow from parent. */
  onClose: () => void;
  formik: any;
  locationOptions: CreateRuleOption[];
  errorTypeOptions: CreateRuleOption[];
  buttonLoader: boolean;
};


const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  open,
  onClose,
  formik,
  locationOptions,
  errorTypeOptions,
  buttonLoader,
}) => {
  const selectedLocationOptions = useMemo(() => {
    const values: string[] = Array.isArray(formik.values.location)
      ? formik.values.location
      : formik.values.location
        ? [formik.values.location]
        : ['all_location']; // Default to "All Locations" if no value is set
    return values.map(
      (v) => locationOptions.find((o) => o.value === v) ?? { label: v, value: v }
    );
  }, [formik.values.location, locationOptions]);

  const handleLocationSelectChange = (opts: any) => {
    console.log("Selected options:", opts);
    if (opts === null) {
      formik.setFieldValue("location", ['all_location']);
    } else if (!Array.isArray(opts)) {
      formik.setFieldValue("location", [opts.value]);
    } else {
      if (opts.length === 0) {
        formik.setFieldValue("location", ['all_location']);
      } 
      else if (opts.some((o: any) => o.value === 'all_location')) {
        opts = [{ label: "All Locations", value: "all_location" }];
        formik.setFieldValue("location", ['all_location']);
      } else{
        formik.setFieldValue(
          "location",
          opts ? opts.map((o: any) => o.value) : []
        );
      }
    }
  }

  return (
    <>
      {/* @ts-expect-error SidePopup prop types are loose in the shared component */}
      <SidePopup
        showModal={open}
        handleClose={onClose}
        title="Create Auto-Delete Rule"
        clearFormik={() => formik.resetForm()}
        closeIcon={false}
        footerStyle="flex justify-end gap-[16px]"
        footerContent={
          <>
            <CustomButton
              buttonStyle="text-[#031227] bg-white hover:bg-[#F5F5F5] px-[26px] py-[12px] text-[14px] font-semibold border-[#E8ECF4]"
              buttonName="Cancel"
              primary={false}
              disable={buttonLoader}
              onClick={onClose}
            />
            <CustomButton
              type="submit"
              loader={buttonLoader}
              primary={true}
              disable={buttonLoader}
              loaderStyle="px-[40px] py-[13px] leading-[17px]"
              buttonStyle="text-white px-[36px] py-[12px] text-[14px] font-semibold hover:bg-[#166B5E] disabled:bg-[#75C8BB]"
              buttonName="Save"
              onClick={() => formik.handleSubmit()}
            />
          </>
        }
      >
        <p className="text-[12px] text-[#68717D] mt-2 mb-4 leading-snug">
          Please select the error type and/or applicable locations,
          and specify the retention period for matching notes prior to deletion.
        </p>
        <div className="pt-[4px] grid grid-cols-1 gap-x-[12px]">
          <div className="w-full col-span-1 mb-[5px]">
            <SelectInput
              label="Error Type"
              name="error_type"
              id="create_rule_error_type"
              placeholder="All Error Types"
              value={formik.values.error_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errors={formik.errors.error_type && formik.touched.error_type}
              disable={buttonLoader}
              options={errorTypeOptions}
              placeholderDisabled={false}
              valueKey="value"
              labelKey="label"
            />
            {formik.errors.error_type && formik.touched.error_type ? (
              <ErrorMessage>{formik.errors.error_type}</ErrorMessage>
            ) : (
              <span>&nbsp;</span>
            )}
          </div>

          <div className="w-full col-span-1 mb-[5px]">
            <label
              htmlFor="create_rule_location"
              className="font-medium text-[12px] text-[#1C2A3D] leading-[100%]"
            >
              Location
              {/* <span className="text-[9px] text-[#68717D] mt-1 leading-snug">
                (if no location is selected, this rule will apply to all locations)
              </span> */}
            </label>
            <div
              className={`mt-[6px] ${formik.errors.location && formik.touched.location
                ? "rounded-[8px] ring-1 ring-red-500"
                : ""
                }`}
            >
              <Select
                className="basic-multi w-full"
                inputId="create_rule_location"
                isMulti = {formik.values.location.includes('all_location') ? false : true}
                isDisabled={buttonLoader}
                isClearable={formik.values.location.includes('all_location') ? false : true}
                isSearchable={true}
                placeholder="Select one or more locations"
                options={locationOptions}
                value={selectedLocationOptions}
                onChange={handleLocationSelectChange}
                onBlur={() => formik.setFieldTouched("location", true)}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                }}
              />
            </div>
            {formik.errors.location && formik.touched.location ? (
              <ErrorMessage>{formik.errors.location}</ErrorMessage>
            ) : (
              <span>&nbsp;</span>
            )}
          </div>

          <div className="w-full col-span-1 mb-[5px]">
            <TextIconInput
              label="Retention Days"
              name="retention_days"
              id="create_rule_retention_days"
              type="number"
              value={formik.values.retention_days}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              errors={formik.errors.retention_days && formik.touched.retention_days}
              placeholder="Enter retention days"
              disable={buttonLoader}
              required
            />
            {formik.errors.retention_days && formik.touched.retention_days ? (
              <ErrorMessage>{formik.errors.retention_days}</ErrorMessage>
            ) : (
              <span>&nbsp;</span>
            )}
          </div>
        </div>
      </SidePopup>
    </>
  );
};

export default CreateRuleModal;
